// LT-294: CRM sync-status dashboard card. Mounted by SettingsCrmPage
// when the workspace has a connection in `active` or `error` state.
// Shows per-entity mapping counts + last-push / last-pull timestamps
// (relative), a recent-errors list, and a force-sync button gated on
// admin (backend returns 403 for members -> we surface the button
// anyway and let the backend enforce; an optional prop `canForceSync`
// hides it from non-admins when the caller knows).
import React, { useEffect, useState } from 'react';
import {
  DashboardEntityType,
  SyncStatusResponse,
  asCrmApiError,
  crmSyncAPI,
} from '../services/crmSyncService';

const ENTITY_ORDER: DashboardEntityType[] = [
  'contact', 'account', 'opportunity', 'activity',
];

const ENTITY_LABEL: Record<DashboardEntityType, string> = {
  contact: 'Contacts',
  account: 'Accounts',
  opportunity: 'Opportunities',
  activity: 'Activities',
};

function formatRelative(iso: string | null): string {
  if (!iso) return 'never';
  const when = Date.parse(iso);
  if (Number.isNaN(when)) return 'unknown';
  const deltaSec = Math.max(0, Math.round((Date.now() - when) / 1000));
  if (deltaSec < 45) return `${deltaSec}s ago`;
  const m = Math.round(deltaSec / 60);
  if (m < 45) return `${m} min ago`;
  const h = Math.round(deltaSec / 3600);
  if (h < 24) return `${h} hr ago`;
  const d = Math.round(deltaSec / 86400);
  return `${d} d ago`;
}

export interface CrmSyncStatusCardProps {
  canForceSync?: boolean;
  onSyncComplete?: () => void;
}

const CrmSyncStatusCard: React.FC<CrmSyncStatusCardProps> = ({
  canForceSync = true,
  onSyncComplete,
}) => {
  const [status, setStatus] = useState<SyncStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await crmSyncAPI.getSyncStatus();
      setStatus(data);
    } catch (e) {
      setError(asCrmApiError(e).message || 'Failed to load sync status.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleForceSync = async () => {
    setSyncing(true);
    setSyncMessage(null);
    setError(null);
    try {
      const stats = await crmSyncAPI.syncNow();
      setSyncMessage(
        `Sync complete: ${stats.seen} seen · ${stats.updated} updated · ${stats.created} created · ${stats.errors} errors.`,
      );
      await load();
      onSyncComplete?.();
    } catch (e) {
      setError(asCrmApiError(e).message || 'Force sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <section
        data-testid="crm-sync-status-card"
        className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 border border-white/10 rounded-lg shadow p-4 mb-6"
      >
        <p className="text-gray-400">Loading sync status…</p>
      </section>
    );
  }

  if (error && !status) {
    return (
      <section
        data-testid="crm-sync-status-card"
        className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 border border-white/10 rounded-lg shadow p-4 mb-6"
      >
        <p className="text-red-300" role="alert">{error}</p>
      </section>
    );
  }

  if (!status) return null;

  return (
    <section
      data-testid="crm-sync-status-card"
      className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 border border-white/10 rounded-lg shadow p-4 mb-6"
    >
      <div className="flex items-center justify-between mb-3 gap-4">
        <h2 className="text-white text-base font-medium">Sync status</h2>
        {canForceSync && (
          <button
            type="button"
            disabled={syncing}
            onClick={handleForceSync}
            className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {syncing ? 'Syncing…' : 'Force sync now'}
          </button>
        )}
      </div>

      {syncMessage && (
        <div
          role="status"
          className="mb-3 rounded border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200"
        >
          {syncMessage}
        </div>
      )}
      {error && status && (
        <div
          role="alert"
          className="mb-3 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200"
        >
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {ENTITY_ORDER.map((et) => (
          <div
            key={et}
            data-testid={`crm-sync-counts-${et}`}
            className="rounded border border-white/10 bg-slate-900/40 px-3 py-2"
          >
            <div className="text-xs uppercase tracking-wide text-gray-400">
              {ENTITY_LABEL[et]}
            </div>
            <div className="text-xl text-white font-medium">
              {status.counts[et] ?? 0}
            </div>
            <div className="text-[11px] text-gray-400 mt-1">
              push · {formatRelative(status.last_pushed_at[et])}
            </div>
            <div className="text-[11px] text-gray-400">
              pull · {formatRelative(status.last_pulled_at[et])}
            </div>
          </div>
        ))}
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-wide text-gray-400 mb-2">
          Recent errors
        </h3>
        {status.recent_errors.length === 0 ? (
          <p className="text-sm text-gray-400">No recent sync errors.</p>
        ) : (
          <ul className="space-y-1" data-testid="crm-sync-errors">
            {status.recent_errors.map((err, idx) => (
              <li
                key={`${err.direction}-${err.when}-${idx}`}
                className="text-xs text-red-300 border-b border-white/5 pb-1"
              >
                <span className="text-gray-400 mr-2">
                  {err.when ? new Date(err.when).toLocaleString() : 'unknown'}
                </span>
                <span className="uppercase text-[10px] tracking-wide text-amber-300 mr-2">
                  {err.direction} · {err.entity_type}
                </span>
                {err.error}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default CrmSyncStatusCard;
